import { useTranslation } from 'react-i18next';
import {
  useSavedFilterViews,
  type SavedFilterView,
  type SavedFilterViews,
} from '~/hooks/useSavedFilterViews';
import { preferenceSlot } from '~/hooks/usePreferenceWriteQueue';
import { savedViewMessages } from '~/utils/savedViewMessages';
import { isOrdersFilterData } from '~/types';
import type {
  GetUserPreferencesQuery,
  TradePerspective,
} from '~/graphql/graphql';
import type { OrderFilterRow } from '~/pages/orders/lib/orderFilters';
import {
  readOrderFilterRows,
  rowsToFilterData,
} from '~/pages/orders/lib/orderFilterStorage';

const ORDERS_VIEW = 'ORDERS' as const;

type Preferences = GetUserPreferencesQuery['userPreferences'] | undefined;

export type OrdersSavedView = SavedFilterView;
export type OrdersSavedViews = SavedFilterViews<OrderFilterRow>;

function activeIdOf(
  preferences: Preferences,
  perspective: TradePerspective
): string | null {
  const id =
    perspective === 'BUYING'
      ? preferences?.buyerOrdersActiveFilterId
      : preferences?.sellerOrdersActiveFilterId;
  return id ?? null;
}

function anonymousOf(
  preferences: Preferences,
  perspective: TradePerspective
): unknown {
  return perspective === 'BUYING'
    ? preferences?.buyerOrdersAnonymousFilter
    : preferences?.sellerOrdersAnonymousFilter;
}

export function useOrdersSavedViews(
  perspective: TradePerspective
): OrdersSavedViews {
  const { t } = useTranslation('order');

  return useSavedFilterViews<OrderFilterRow>({
    view: ORDERS_VIEW,
    perspective,
    slot: preferenceSlot('orders', perspective),
    isOwnFilterData: isOrdersFilterData,
    readRows: readOrderFilterRows,
    rowsToFilterData,
    activeIdOf,
    anonymousOf,
    anonymousPreferences: (rows, forPerspective) =>
      forPerspective === 'BUYING'
        ? { buyerOrdersAnonymousFilter: rowsToFilterData(rows) }
        : { sellerOrdersAnonymousFilter: rowsToFilterData(rows) },
    messages: savedViewMessages(t, 'order.filter'),
  });
}
